/**
 * Smart wrapper for an inline-editable analysis field. Owns the patchAnalysis
 * API call, draft + saving state, and renders the FieldEditHistory modal
 * when the user opens the history marker. EditableAnalysisFieldView renders
 * the actual UI.
 */

import { useCallback, useEffect, useState } from "react"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { patchAnalysis } from "../../lib/api"
import { FieldEditHistory } from "../FieldEditHistory"
import { EditableAnalysisFieldView } from "./EditableAnalysisFieldView"
import type { IProps } from "./interfaces"

export function EditableAnalysisField({
  meetingId,
  fieldPath,
  value,
  canEdit,
  isEdited,
  onSaved,
}: IProps): React.JSX.Element {
  const company = useCompanyApi()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)

  useEffect(() => {
    setDraft(value)
  }, [value])

  const handleStartEdit = useCallback((): void => {
    setEditing(true)
  }, [])

  const handleCancel = useCallback((): void => {
    setDraft(value)
    setEditing(false)
  }, [value])

  const handleCommit = useCallback(async (): Promise<void> => {
    if (!company) return
    const trimmed = draft.trim()
    if (!trimmed || trimmed === value.trim()) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await patchAnalysis(company, meetingId, fieldPath, trimmed)
      setEditing(false)
      onSaved?.()
    } catch {
      setDraft(value)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }, [company, draft, value, meetingId, fieldPath, onSaved])

  const handleRequestHistory = useCallback((): void => {
    setHistoryOpen(true)
  }, [])

  const handleCloseHistory = useCallback((): void => {
    setHistoryOpen(false)
  }, [])

  return (
    <>
      <EditableAnalysisFieldView
        value={value}
        canEdit={canEdit}
        isEdited={isEdited}
        editing={editing}
        draft={draft}
        saving={saving}
        onStartEdit={handleStartEdit}
        onDraftChange={setDraft}
        onCommit={handleCommit}
        onCancel={handleCancel}
        onRequestHistory={handleRequestHistory}
      />
      {historyOpen && (
        <FieldEditHistory
          meetingId={meetingId}
          fieldPath={fieldPath}
          opened={historyOpen}
          onClose={handleCloseHistory}
        />
      )}
    </>
  )
}
